import type { FC, ReactNode } from 'react'

const RULE_BASE = 'pointer-events-none absolute inset-x-0 bottom-0 h-px'

const RULE_ACTIVE = `${RULE_BASE} origin-left scale-x-0 bg-fz-rule-on transition-transform duration-500 ease-[cubic-bezier(0.215,0.61,0.355,1)] peer-focus:scale-x-100 data-[filled=true]:scale-x-100 data-[invalid=true]:scale-x-100 data-[invalid=true]:bg-fz-alert`

type InlineFieldProps = {
    name: string
    label: string
    placeholder: string
    value: string
    onChange: (value: string) => void
    type?: 'text' | 'email'
    autoComplete?: string
    invalid?: boolean
    minChars?: number
}

/**
 * An input set inline in a sentence. The hidden mirror sits in the same grid
 * cell as the input, so the field sizes itself to its content with no
 * measurement and no layout effect.
 */
export const InlineField: FC<InlineFieldProps> = ({
    name,
    label,
    placeholder,
    value,
    onChange,
    type = 'text',
    autoComplete,
    invalid = false,
    minChars = 6
}): ReactNode => {
    return (
        <span className='relative inline-grid align-baseline'>
            <span
                aria-hidden='true'
                className='invisible col-start-1 row-start-1 whitespace-pre px-1 pb-1'
                style={{ minWidth: `${minChars}ch` }}
            >
                {value || placeholder}
            </span>

            <input
                id={name}
                name={name}
                type={type}
                value={value}
                placeholder={placeholder}
                aria-label={label}
                aria-invalid={invalid || undefined}
                autoComplete={autoComplete}
                spellCheck={type === 'email' ? false : undefined}
                onChange={event => onChange(event.target.value)}
                className='peer col-start-1 row-start-1 w-full bg-transparent px-1 pb-1 text-fz-ink outline-none placeholder:text-fz-faint'
            />

            <span aria-hidden='true' className={`${RULE_BASE} bg-fz-rule`} />

            <span
                aria-hidden='true'
                data-filled={value.length > 0}
                data-invalid={invalid}
                className={RULE_ACTIVE}
            />
        </span>
    )
}

type InlineAreaProps = {
    name: string
    label: string
    placeholder: string
    value: string
    onChange: (value: string) => void
    invalid?: boolean
}

/** Same trick, wrapping to as many lines as the writing needs. */
export const InlineArea: FC<InlineAreaProps> = ({
    name,
    label,
    placeholder,
    value,
    onChange,
    invalid = false
}): ReactNode => {
    return (
        <span className='relative grid w-full'>
            <span
                aria-hidden='true'
                className='invisible col-start-1 row-start-1 whitespace-pre-wrap break-words px-1 pb-2'
            >
                {`${value || placeholder}\n`}
            </span>

            <textarea
                id={name}
                name={name}
                rows={1}
                value={value}
                placeholder={placeholder}
                aria-label={label}
                aria-invalid={invalid || undefined}
                onChange={event => onChange(event.target.value)}
                className='peer col-start-1 row-start-1 w-full resize-none overflow-hidden bg-transparent px-1 pb-2 text-fz-ink outline-none placeholder:text-fz-faint'
            />

            <span aria-hidden='true' className={`${RULE_BASE} bg-fz-rule`} />

            <span
                aria-hidden='true'
                data-filled={value.length > 0}
                data-invalid={invalid}
                className={RULE_ACTIVE}
            />
        </span>
    )
}
